import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import ForcePasswordChangeModal from './components/ForcePasswordChangeModal';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ChangePassword from './pages/ChangePassword';
import AdminDashboard from './pages/AdminDashboard';
import TransportDashboard from './pages/TransportDashboard';
import DriverDashboard from './pages/DriverDashboard';
import ParentDashboard from './pages/ParentDashboard';
import SchoolManagement from './pages/SchoolManagement';
import ComplaintsPage from './pages/ComplaintsPage';
import TransportStats from './pages/TransportStats';
import TripAnalytics from './pages/TripAnalytics';
import Profile from './pages/Profile';
import AboutUs from './pages/AboutUs';
import Terms from './pages/Terms';
import ITDashboard from './pages/ITDashboard';
import Subscriptions from './pages/Subscriptions';
import FinanceTracker from './pages/FinanceTracker';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  // Show force password change modal if needed
  if (user.force_password_change) {
    return (
      <>
        {children}
        <ForcePasswordChangeModal isOpen={true} />
      </>
    );
  }

  return children;
};

const DashboardRouter = () => {
  const { user } = useAuth();

  if (!user) return <Navigate to="/login" replace />;

  switch (user.role) {
    case 'it':
      return <ITDashboard />;
    case 'master_admin':
    case 'admin':
      return <AdminDashboard />;
    case 'transport':
      return <TransportDashboard />;
    case 'driver':
      return <DriverDashboard />;
    case 'parent':
      return <ParentDashboard />;
    default:
      return <Navigate to="/login" replace />;
  }
};

const HomeRoute = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  if (!user) return <AboutUs />;
  return (
    <ProtectedRoute>
      <DashboardRouter />
    </ProtectedRoute>
  );
};

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route
            path="/change-password"
            element={
              <ProtectedRoute>
                <ChangePassword />
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<HomeRoute />} />
          <Route
            path="/subscriptions"
            element={
              <ProtectedRoute allowedRoles={['admin', 'master_admin']}>
                <Subscriptions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/finance"
            element={
              <ProtectedRoute allowedRoles={['driver', 'transport']}>
                <FinanceTracker />
              </ProtectedRoute>
            }
          />
          <Route
            path="/schools"
            element={
              <ProtectedRoute allowedRoles={['admin', 'master_admin']}>
                <SchoolManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/complaints"
            element={
              <ProtectedRoute allowedRoles={['admin', 'master_admin', 'parent', 'driver', 'transport']}>
                <ComplaintsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/transport-stats"
            element={
              <ProtectedRoute allowedRoles={['admin', 'master_admin']}>
                <TransportStats />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trip-analytics"
            element={
              <ProtectedRoute allowedRoles={['transport', 'admin', 'master_admin']}>
                <TripAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
